import { message } from 'antd';
import type { MessageBundle } from '@amzn/arb-tools';
import { safeGetMessage } from '@xbcb/ui-utils';
import { EudrVerificationStatus } from '@xbcb/work-order-types';
import {
  executeMutation,
  type RefetchQueriesInclude,
} from 'libs/executeMutation';
import {
  eudrEntryHasInput,
  eudrEntryIdentity,
  eudrEntryIsSubmittable,
  type EudrEntryStatusInput,
} from 'libs/formCore/schemas';
import { SUBMIT_EUDR_DDS_VERIFICATION } from 'libs/sharedQueries/mutations/submitEudrDdsVerification.query';

const RESUBMITTABLE_STATUSES: (string | null | undefined)[] = [
  null,
  undefined,
  EudrVerificationStatus.NOT_STARTED,
  EudrVerificationStatus.FAILED,
  EudrVerificationStatus.TIMEOUT,
];

export interface SubmitEudrDdsVerificationArgs {
  customsDeclarationId: string;
  goodsItemSequence?: number;
  bundle: MessageBundle;
  submittableCount: number;
  incompleteCount?: number;
  refetchQueries?: RefetchQueriesInclude;
}

export const isEudrEntryVerifiable = (entry?: EudrEntryStatusInput): boolean =>
  !!entry &&
  RESUBMITTABLE_STATUSES.includes(entry.status) &&
  eudrEntryIsSubmittable(entry);

export const areEudrEntriesSaved = (
  current: EudrEntryStatusInput[],
  persisted: EudrEntryStatusInput[],
): boolean => {
  const identities = (entries: EudrEntryStatusInput[]) =>
    entries.filter(eudrEntryHasInput).map(eudrEntryIdentity);
  const currentIdentities = identities(current);
  const persistedIdentities = identities(persisted);
  return (
    currentIdentities.length === persistedIdentities.length &&
    currentIdentities.every((id, index) => id === persistedIdentities[index])
  );
};

export const countIncompleteEudrEntries = (
  entries: EudrEntryStatusInput[],
): number =>
  entries.filter(
    (entry) =>
      RESUBMITTABLE_STATUSES.includes(entry.status) &&
      !eudrEntryIsSubmittable(entry),
  ).length;

interface GoodsShipmentEudrEntries {
  goodsItem?:
    | { eudrDueDiligenceStatements?: EudrEntryStatusInput[] | null }[]
    | null;
}

export const eudrEntriesOfDeclaration = (
  goodsShipments: GoodsShipmentEudrEntries[] | null | undefined,
): EudrEntryStatusInput[] =>
  (goodsShipments ?? []).flatMap((shipment) =>
    (shipment?.goodsItem ?? []).flatMap(
      (item) => item?.eudrDueDiligenceStatements ?? [],
    ),
  );

export const verificationSubmittedMessage = (
  submittableCount: number,
  bundle: MessageBundle,
): string =>
  safeGetMessage(bundle, 'eudr_entries_submitted', {
    count: submittableCount,
  });

export const incompleteSkippedMessage = (
  incompleteCount: number,
  bundle: MessageBundle,
): string =>
  safeGetMessage(bundle, 'eudr_incomplete_skipped', {
    count: incompleteCount,
  });

export const submitEudrDdsVerification = async ({
  customsDeclarationId,
  goodsItemSequence,
  bundle,
  submittableCount,
  incompleteCount = 0,
  refetchQueries,
}: SubmitEudrDdsVerificationArgs): Promise<boolean> => {
  let submitted = false;
  await executeMutation({
    mutation: SUBMIT_EUDR_DDS_VERIFICATION,
    variables: {
      idInput: { id: customsDeclarationId },
      goodsItemSequence,
    },
    refetchQueries,
    successMessage: '',
    onSuccess: async () => {
      submitted = true;
      void message.success(
        verificationSubmittedMessage(submittableCount, bundle),
      );
      if (incompleteCount) {
        void message.warning(incompleteSkippedMessage(incompleteCount, bundle));
      }
    },
  });
  return submitted;
};
